import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Spinner, Form } from "react-bootstrap";
import "../../modules/Users/AllUsers/index.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPostComments,
  updatePostComment,
  deletePostComment,
  clearComments,
} from "../../redux/slice/postCommentSlice";
import { EditIcon, Delete02Icon } from "../../assets/icons";
import { fromNow } from "../../utils/dateHelper";
import DeletePopup from "../delete-popup";
import withModal from "../withModal";
import { useToast } from "../../context/TostContext";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "../../routes/pageRoutes";
import { UserInital } from "../../utils/otherHelopers";

const PostCommentsModalInner = ({ onClose, postId }) => {
  const dispatch = useDispatch();
  const { comments, commentsLoading, deleteCommentLoading } = useSelector(
    (state) => state.postComments
  );
  const toast = useToast();
  const navigate = useNavigate();
  const {id} = useParams();

  const handleAuthorClick = (authorData, authorType) => {
    if (!authorData) return;
    if (authorType === "company") {
      navigate(routes.viewCompany.replace(":id", authorData.id));
    } else {
      if (id === authorData.id) {
        onClose();
      } else {
        navigate(routes.viewUser.replace(":id", authorData.id));
      }
    }
  };
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const loaderRef = useRef(null);
  const lastFetchedPage = useRef(1);
  const debouncedSearchTimeout = useRef(null);

  useEffect(() => {
    if (postId) {
      if (debouncedSearchTimeout.current) clearTimeout(debouncedSearchTimeout.current);

      debouncedSearchTimeout.current = setTimeout(() => {
        setPage(1);
        setHasMore(true);
        lastFetchedPage.current = 1;

        dispatch(fetchPostComments({ postId, page: 1, limit: 10, search: searchQuery })).then(
          (res) => {
            if (res.payload?.data?.length < 10) {
              setHasMore(false);
            }
          }
        );
      }, 500);
    }

    return () => {
      if (debouncedSearchTimeout.current) clearTimeout(debouncedSearchTimeout.current);
    };
  }, [postId, dispatch, searchQuery]);

  useEffect(() => {
    return () => {
      dispatch(clearComments());
    };
  }, [dispatch]);

  const loadMoreComments = useCallback(() => {
    const nextPage = page + 1;
    if (commentsLoading || !hasMore || lastFetchedPage.current >= nextPage) return;

    lastFetchedPage.current = nextPage;
    dispatch(fetchPostComments({ postId, page: nextPage, limit: 10, search: searchQuery })).then(
      (res) => {
        const newComments = res.payload?.data || [];
        if (newComments.length === 0) {
          setHasMore(false);
        } else {
          setPage(nextPage);
        }
      }
    );
  }, [dispatch, postId, page, commentsLoading, hasMore, searchQuery]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !commentsLoading) {
          loadMoreComments();
        }
      },
      { root: null, rootMargin: "20px", threshold: 1.0 }
    );

    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, commentsLoading, loadMoreComments]);

  const handleEditClick = (comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async (commentId) => {
    if (!editContent.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }
    try {
      await dispatch(
        updatePostComment({ commentId, content: editContent })
      ).unwrap();
      toast.success("Comment updated successfully");
      setEditingCommentId(null);
      setEditContent("");
    } catch (err) {
      toast.error(err?.message || "Failed to update comment");
    }
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditContent("");
  };

  const handleDeleteClick = (comment) => {
    setCommentToDelete(comment);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (commentToDelete) {
        await dispatch(
          deletePostComment({ commentId: commentToDelete.id })
        ).unwrap();
        toast.success("Comment deleted successfully");
        setShowDeleteModal(false);
        setCommentToDelete(null);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete comment");
    }
  };

  return (
    <>
      <Modal.Body style={{ minHeight: "300px" }}>
        <div className="mb-3">
          <Form.Control
            type="search"
            placeholder="Search comments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-100"
          />
        </div>
        {comments.length === 0 && !commentsLoading ? (
          <div className="text-center mt-5 text-muted">
            No comments available for this post.
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {comments.map((comment) => {
              const authorData = comment.user || comment.company;
              const authorName =
                comment.author_type === "company"
                  ? authorData?.name
                  : authorData
                    ? `${authorData.first_name} ${authorData.last_name}`
                    : "Unknown User";

              const authorAvatar =
                comment.author_type === "company"
                  ? authorData?.profile?.logo?.url
                  : authorData?.profile?.profile_photo?.url;

              return (
                <div
                  key={comment.id}
                  className="d-flex gap-3 border-bottom pb-3 mt-2"
                >
                  <UserInital width={40} image={authorAvatar} name={authorName} className="cursor-pointer"
                    onClick={() => handleAuthorClick(authorData, comment.author_type)} />

                  <div className="w-100">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <h6
                            className="mb-0 fw-semibold cursor-pointer"
                            onClick={() => handleAuthorClick(authorData, comment.author_type)}
                          >
                            {authorName}
                          </h6>
                          <small className="text-muted">
                            {fromNow(comment.created_at)}
                          </small>
                        </div>
                        {comment.author_type === "user" && comment.user?.profile?.profession?.name && (
                          <small className="text-muted d-block">
                            {comment.user.profile.profession.name}
                          </small>
                        )}
                        {comment.author_type === "company" && comment.company?.profile?.city?.name && (
                          <small className="text-muted d-block">
                            {comment.company.profile.city.name}
                          </small>
                        )}
                      </div>
                      <div className="ms-auto d-flex gap-2">
                        <button
                          className="post-action-btn edit-btn"
                          onClick={() => handleEditClick(comment)}
                          title="Edit Comment"
                        >
                          <EditIcon width={18} height={18} color="#007bff" />
                        </button>
                        <button
                          className="post-action-btn delete-btn"
                          onClick={() => handleDeleteClick(comment)}
                          title="Delete Comment"
                        >
                          <Delete02Icon size={18} />
                        </button>
                      </div>
                    </div>

                    {editingCommentId === comment.id ? (
                      <div className="mt-2">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="mb-2"
                        />
                        <div className="d-flex gap-2 justify-content-end">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSaveEdit(comment.id)}
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-dark mb-0" style={{ whiteSpace: "pre-line" }}>{comment.content}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div ref={loaderRef} style={{ height: "1px" }}></div>
        {commentsLoading && (
          <div className="text-center mt-3">
            <Spinner animation="border" variant="primary" size="sm" />
            <span className="ms-2 text-muted">Loading comments...</span>
          </div>
        )}
      </Modal.Body>

      <DeletePopup
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        content="Are you sure you want to delete this comment? This action cannot be undone."
        saveTitle="Delete"
        disabled={deleteCommentLoading}
      />
    </>
  );
};

export default withModal(PostCommentsModalInner);
